import {
  buildPath,
  buildTemplatePath,
  Card,
  CloseButton,
  deepCopy,
  ListContext,
  ListContextValue,
  newToast,
  Page,
  PageRoute,
  pn,
  Stack,
  Typography,
  useUnsavedChanges,
  useValidationErrors,
} from "@compass/core";
import { SubmitButton } from "components";
import {
  BulkSecondLevelValidation,
  BulkSelectedItemErrors,
  Contributor,
  ContributorBulkCreateMatches,
  ContributorMatch,
} from "generated/openApi";
import produce from "immer";
import { useContext, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useLocation } from "react-router-dom";
import { setAppToast } from "redux/reducers/appSlice";
import ApiService from "services/apiService";
import { MatchInfo } from "./MatchInfo";
import { SelectedContributors } from "./SelectedContributors";

const routeName = "contributorbulkCreateMatches";

export const contributorBulkCreateMatchesPageRoute: PageRoute = {
  handle: "New Time & Effort",
  templatePath: buildTemplatePath(routeName),
  getPath: () => buildPath(routeName),
};

export type ContributorBulkCreateMatchesPageState = {
  selectedIds: number[];
};

export const ContributorBulkCreateMatchesPage = () => {
  const dispatch = useDispatch();
  const listContext = useContext<ListContextValue>(ListContext);
  const { state } = useLocation();
  const [stateParam] = useState<ContributorBulkCreateMatchesPageState>(state);
  const [bulkCreateMatches, setBulkCreateMatches] = useState<ContributorBulkCreateMatches>({});
  const { unsavedChanges, resetUnsavedChanges } = useUnsavedChanges({ trackedItems: [bulkCreateMatches] });
  const { validationErrors, setValidationErrors } = useValidationErrors();
  const [selectedItemsErrors, setSelectedItemsErrors] = useState<BulkSelectedItemErrors[]>([]);

  useEffect(() => {
    getBulkCreateMatches(stateParam.selectedIds);
  }, []);

  const getBulkCreateMatches = (contributorIds: number[]) => {
    ApiService.contributors.getBulkCreateMatches(contributorIds).then((response) => {
      setBulkCreateMatches(response);
      resetUnsavedChanges([response]);
    });
  };

  const save = (bulkCreateMatches: ContributorBulkCreateMatches) => {
    setValidationErrors({});
    setSelectedItemsErrors([]);

    let savingBulkCreateMatches = deepCopy(bulkCreateMatches);

    // if creating multiple dates: placeholder date: since date is required in the model but we are not actually using it here
    if (savingBulkCreateMatches?.match?.creatingBulkDates) {
      savingBulkCreateMatches.match.date = new Date();
    }

    ApiService.contributors
      .saveBulkCreateMatches(savingBulkCreateMatches)
      .then((response) => {
        const bulkSecondLevelValidation: BulkSecondLevelValidation | undefined =
          response as unknown as BulkSecondLevelValidation;

        if (bulkSecondLevelValidation && bulkSecondLevelValidation.badRequest) {
          dispatch(setAppToast(newToast("error")));
          setSelectedItemsErrors(bulkSecondLevelValidation.selectedItemsErrors || []);
        } else {
          dispatch(setAppToast(newToast("success")));
          resetUnsavedChanges([bulkCreateMatches]);
          listContext.loadList?.();
          listContext.clearSelectedIds?.();
        }
      })
      .catch((ex) => {
        dispatch(setAppToast(newToast("error")));
        setValidationErrors(ex.response);
      });
  };

  return (
    <Page
      isForm
      header={
        <Stack direction="row" justifyContent="space-between">
          <Typography variant="inherit">{contributorBulkCreateMatchesPageRoute.handle}</Typography>

          <Stack direction="row" alignItems="center" spacing="sm">
            <CloseButton confirm={unsavedChanges} />

            <SubmitButton onClick={() => save(bulkCreateMatches)} disabled={!unsavedChanges} />
          </Stack>
        </Stack>
      }
    >
      <Stack spacing="lg">
        <Card header="Matches">
          <Stack direction="column" spacing="md">
            <MatchInfo
              match={bulkCreateMatches.match || ({} as ContributorMatch)}
              setMatch={(value) =>
                setBulkCreateMatches(
                  produce(bulkCreateMatches, (draft) => {
                    draft.match = value;
                  })
                )
              }
              validationErrors={validationErrors[pn<ContributorBulkCreateMatches>("match")] || {}}
            />
          </Stack>
        </Card>

        <SelectedContributors
          contributors={new Map(bulkCreateMatches.contributors?.map((x) => [x.id, x]))}
          setContributors={(value) =>
            setBulkCreateMatches({ ...bulkCreateMatches, contributors: Array.from(value.values()) })
          }
          errors={selectedItemsErrors}
          setErrors={setSelectedItemsErrors}
        />
      </Stack>
    </Page>
  );
};
